import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  FormGroup,
  Input,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaFilter,
  FaSyncAlt,
  FaBarcode,
} from "react-icons/fa";
import { FaReceipt } from "react-icons/fa";
import { reglementActions } from "../../sagas/reglementSlice";
import { formatPrice, formatTime } from "../../helpers/utility";
import { useLocation, useNavigate } from "react-router-dom";
import { clientActions } from "../../sagas/clientSlice";
import { debounce } from "lodash";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { soldeActions } from "../../sagas/soldeSlice";
import {
  CBL_PAGE,
  CPMT_INVOICES_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  CPMT_TRANSFER_PAGE,
  PAY_FORM_EDIT,
  PAY_FORM_NEW,
  PAY_PAGE,
  RETOUR_PAGE,
} from "../../routes/routeConstants";
import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";

import "./AvanceClientListPOS.css";
import { shiftActions } from "../../sagas/shiftSlice";

const AvanceClientList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading, error, list } = useSelector((state) => state.reglements);
  const { clients } = useSelector((state) => state.clients);
  const { company, businessName, email, address, telephone, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
  const { summary, list: shifts, currentShift, loading: shiftLoading, error: shiftError } = useSelector((state) => state.shift);
  const [activeTab, setActiveTab] = useState("AVANCE");

  // Dates default
  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const pad = (n) => n.toString().padStart(2, "0");
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [clientModal, setClientModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: commercialId,
    dateFrom: formatDate(todayDate),
    dateTo: formatDate(endOfMonth),
    statut: null,
    query: "",
  });

  // POS Search keypad
  const searchInputRef = useRef(null);
  const [showSearchKeypad, setShowSearchKeypad] = useState(false);
  const latestFiltersRef = useRef(filters);

  useEffect(() => {
    // F2 focus search
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setShowSearchKeypad(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    dispatch(shiftActions.fetchSummary({ shiftId: currentShift?.id }));
    if (!clients || clients.length === 0) {
      dispatch(clientActions.search({ query: "" }));
    }

    dispatch(shiftActions.fetchList({
      commercialId: commercialId,
      pointOfSaleId: pointOfSaleId,
    }))
  }, [currentShift])

  useEffect(() => {
    dispatch(reglementActions.avanceClient({ ...filters }));
    dispatch(shiftActions.fetchSummary({ shiftId: currentShift?.id }));
  }, [filters.commercialId, filters.clientId, filters.dateFrom, filters.dateTo, filters.query]);

  const handleReload = () => {
    dispatch(reglementActions.avanceClient(filters));
  };

  const handleEdit = (reglement) => {
    navigate(PAY_FORM_EDIT, { state: { reglement } });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        dispatch(
          reglementActions.avanceClient({
            ...latestFiltersRef.current,
            query,
          })
        );
      }, 250),
    [dispatch]
  );

  const selectedClient = useMemo(() => clients.find((c) => c.id === filters.clientId), [clients, filters.clientId]);

  useEffect(() => {
    latestFiltersRef.current = filters;
  }, [filters]);


  // =========================
  // SOLDE BEFORE/AFTER helpers
  // =========================
  const parseAnyDate = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      if (/^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v + "T00:00:00");
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) {
        const [dd, mm, yyyy] = v.split("-").map(Number);
        return new Date(yyyy, mm - 1, dd);
      }
    }
    if (v instanceof Date) return v;
    return new Date(v);
  };

  const normalizeRef = (s) => (s || "").toString().trim().toUpperCase();
  const isSameRef = (a, b) => normalizeRef(a) === normalizeRef(b);

  const getReglementSignedAmount = (reglement) => {
    const montant = Number(reglement?.montantRegle || 0);
    const type = (reglement?.typeReglement || "").toString().toUpperCase();
    if (type === "AVANCE" || type === "PAIEMENT" || type === "REGLEMENT") {
      return { credit: montant, debit: 0 };
    }
    if (type === "BON_LIVRAISON" || type === "BL") {
      return { credit: 0, debit: montant };
    }
    return { credit: 0, debit: 0 };
  };

  const computeSoldeAvantApresFromMouvements = (reglement, mouvements = []) => {
    if (!mouvements || mouvements.length === 0) {
      return { soldeAvant: 0, soldeApres: 0 };
    }

    const refReglement = normalizeRef(reglement?.reference || reglement?.id);
    const mvtReglement = mouvements.find((m) => isSameRef(m?.reference, refReglement));

    if (mvtReglement) {
      return {
        soldeAvant: Number(mvtReglement.soldeAvant || 0),
        soldeApres: Number(mvtReglement.soldeApres || 0),
      };
    }

    const dReglement = parseAnyDate(reglement?.dateReglement);
    const sorted = [...mouvements].sort((a, b) => {
      const da = parseAnyDate(a?.dateOperation || a?.date_mouvement);
      const db = parseAnyDate(b?.dateOperation || b?.date_mouvement);
      return da - db;
    });

    let lastBefore = null;
    for (const m of sorted) {
      const dm = parseAnyDate(m?.dateOperation || m?.date_mouvement);
      if (!dm || !dReglement) continue;
      if (dm.getTime() <= dReglement.getTime()) lastBefore = m;
    }

    const baseAvant = lastBefore ? Number(lastBefore.soldeApres || 0) : 0;
    const { credit, debit } = getReglementSignedAmount(reglement);
    const soldeApres = baseAvant + Number(credit || 0) - Number(debit || 0);

    return { soldeAvant: baseAvant, soldeApres };
  };


  // =========================
  // UI helpers
  // =========================
  const getStatutBadge = (statut) => {
    const badges = {
      EN_ATTENTE: { color: "info", icon: FaClock },
      ENCAISSE: { color: "success", icon: FaCheckCircle },
      IMPAYE: { color: "danger", icon: FaTimes },
      ANNULE: { color: "warning", icon: FaTimes },
    };

    const badge = badges[statut] || { color: "secondary", icon: FaClock };
    const Icon = badge.icon;

    return (
      <Badge color={badge.color} className="pos-status-badge">
        <Icon className="me-1" size="12" />
        {t(`reglement.${statut.toLowerCase()}`)}
      </Badge>
    );
  };

  const clearFilters = () => {
    setFilters({
      query: "",
      clientId: null,
      commercialId: commercialId,
      dateFrom: formatDate(todayDate),
      dateTo: formatDate(endOfMonth),
    });
  };

  const getModeReglementIcon = (mode) => {
    const icons = {
      ESPECES: "💵",
      CHEQUE: "📝",
      VIREMENT: "🏦",
      CARTE_BANCAIRE: "💳",
      TRAITE: "📄",
      AUTRE: "❓",
    };
    return icons[mode] || "❓";
  };

  const handleEncaisserReglement = (reglement) => {
    Swal.fire({
      title: t('common.confirm'),
      text: t('reglement.confirmEncaissement'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('common.yes'),
      cancelButtonText: t('common.no'),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(reglementActions.encaisserReglement({
          id: reglement.id,
          dateEncaissement: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('reglement.encaissementSuccess'),
            });

            handleReload(); // Refresh the filteredList

          }
        }));
      }
    });
  };

  const getBLNumbers = (reglement) => {
    if (!reglement?.numBonLivraisons) return [];

    // If backend sends string: "BL001, BL002"
    if (typeof reglement.numBonLivraisons === "string") {
      return reglement.numBonLivraisons
        .split(",")
        .map(n => n.trim())
        .filter(Boolean);
    }

    // If backend sends array already
    if (Array.isArray(reglement.numBonLivraisons)) {
      return reglement.numBonLivraisons;
    }

    return [];
  };



  // =========================
  // Render
  // =========================
  return (
    <Container fluid className="pos-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onCloseShift={clearFilters}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "INVOICES") navigate(CPMT_INVOICES_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
      />
      {summary && (
        <Card className="pos-shift-summary shadow-sm border-0">
          <CardBody className="d-flex align-items-center justify-content-between flex-wrap gap-3">

            {/* LEFT – Shift identity */}
            <div className="shift-meta">
              <div className="shift-title">
                <strong>{summary.commercialName || fullName}</strong>
                <Badge
                  color={summary.status === "OUVERT" ? "success" : "secondary"}
                  className="ms-2"
                >
                  {summary.status}
                </Badge>
              </div>

              <div className="shift-sub text-muted">
                Ouvert à : <strong>{formatTime(summary.openingTime)}</strong>
              </div>
            </div>

            {/* RIGHT – KPIs */}
            <div className="shift-kpis">
              <div className="shift-kpi">
                <small>Ventes</small>
                <strong>{summary?.numberOfSales ?? 0}</strong>
              </div>

              <div className="shift-kpi">
                <small>Total ventes</small>
                <strong>{formatPrice(summary?.totalSalesAmount ?? 0, false)}</strong>
              </div>

              {/* <div className="shift-kpi">
                <small>Total encaissé</small>
                <strong className="text-success">
                  {formatPrice(summary?.totalPaymentsAmount ?? 0, false)}
                </strong>
              </div> */}
            </div>

          </CardBody>
        </Card>
      )}

      {/* ✅ POS TOP BAR */}
      <div className="pos-topbar shadow-sm">
        <div className="pos-search">
          <FaSearch className="icon" />

          <Input
            innerRef={searchInputRef}
            value={filters.query}
            placeholder={t("reglement.searchPlaceholder")}
            readOnly
            inputMode="none"
            onKeyDown={(e) => e.preventDefault()}
            className="pos-search-input pos-nokeyboard"
            onClick={() => setShowSearchKeypad(true)}
          />

          <FaBarcode className="barcode" />
        </div>


        <div className="pos-actions">
            <div className="pos-action-stack">
              <Label className="pos-label pos-label-spacer">.</Label>
              <Button
                className="pos-btn pos-btn-light"
                color="light"
                onClick={() => setClientModal(true)}
              >
                Client : <strong className="ms-2">{selectedClient ? (selectedClient.typeClient === "PARTICULIER" ? `${selectedClient.nom} ${selectedClient.prenom || ""}` : selectedClient.entreprise) : "Tous"}</strong>
              </Button>
            </div>
	          <div className="pos-date-range">
	            <FormGroup className="mb-0 pos-date-field">
              <Label className="pos-label">{t("common.from", "Du")}</Label>
              <Input
                type="date"
                className="form-control pos-input"
                value={filters.dateFrom || ""}
                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || null }))}
              />
            </FormGroup>
	            <FormGroup className="mb-0 pos-date-field">
              <Label className="pos-label">{t("common.to", "Au")}</Label>
              <Input
                type="date"
                className="form-control pos-input"
                value={filters.dateTo || ""}
                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || null }))}
              />
            </FormGroup>
          </div>

          <div className="pos-action-stack">
            <Label className="pos-label pos-label-spacer">.</Label>
            <Button className="pos-btn pos-btn-light" onClick={handleReload} title="Rafraîchir">
              <FaSyncAlt />
            </Button>
          </div>

          <div className="pos-action-stack">
            <Label className="pos-label pos-label-spacer">.</Label>
            <Button className="pos-btn pos-btn-primary" onClick={() => navigate(PAY_FORM_NEW)}>
              <FaPlus /> <span className="d-none d-md-inline ms-2">{t("delivery.new")}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* LIST */}
      <Card className="pos-list-card shadow-sm border-0">
        <CardBody className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t("common.loading")}</div>
            </div>
          ) : error ? (
            <div className="text-center py-5 text-danger">{t("common.error")} : Erreur lors du chargement</div>
          ) : list?.length === 0 ? (
            <div className="text-center py-5 text-muted">{t("reglement.noReglements")}</div>
          ) : (
            <div className="pos-list">
              {list.map((reglement) => (
                <div key={reglement.id} className="pos-list-row d-flex justify-content-between align-items-start flex-wrap p-2 border-bottom">
                  {/* LEFT SIDE */}
                  <div className="left flex-grow-1">
                    <div className="top d-flex align-items-center gap-2 mb-1">
                      <strong className="numero">{reglement.reference || reglement.id}</strong>
                      {getStatutBadge(reglement.statut)}
                    </div>

                    <div className="meta d-flex flex-wrap gap-3 small text-muted mb-1">
                      <span><FaUser className="me-1" /> {reglement.nomClient || "Client"}</span>
                      <span><FaCalendarAlt className="me-1" /> {new Date(reglement.dateReglement).toLocaleDateString("fr-FR")}</span>
                    </div>

                    <div className="amounts d-flex gap-3 small">
                      <span className="ttc">Montant : {formatPrice(reglement.montantRegle)}</span>
                      {reglement.montantAffecte > 0 && <span className="paye">Affecté : {formatPrice(reglement.montantAffecte)}</span>}
                    </div>
                    {getBLNumbers(reglement).length > 0 && (
                      <div className="mt-1 small text-muted">
                        <FaReceipt className="me-1" />
                        BL :
                        {getBLNumbers(reglement).map((bl, index) => (
                          <Badge
                            key={index}
                            color="secondary"
                            className="ms-1 border"
                          >
                            {bl}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {reglement.modeReglement && (
                      <div className="mt-1 small text-muted">
                        <span className="me-1">{getModeReglementIcon(reglement.modeReglement)}</span>
                        {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
                      </div>
                    )}
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="right d-flex gap-1 mt-2 flex-wrap">
                    {/* <Button
                      className="pos-icon-btn pos-pdf"
                      title="Reçu"
                      onClick={() => handleGenerateReceipt(reglement)}
                    >
                      <FaReceipt />
                    </Button> */}

                    <Button
                      className="pos-icon-btn pos-edit"
                      title={t("reglement.edit")}
                      onClick={() => handleEdit(reglement)}
                    >
                      <FaEdit />
                    </Button>
                    {reglement.statut === 'EN_ATTENTE' && <Button
                      color="success"
                      title={t('bonLivraison.livrer')}
                      onClick={() => handleEncaisserReglement(reglement)}
                      className="pos-icon-btn pos-confirm"
                      size="sm"
                    >
                      <FaCheckCircle />
                    </Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* SEARCH KEYPAD */}
      <POSKeypadModal
        isOpen={showSearchKeypad}
        toggle={() => setShowSearchKeypad(false)}
        value={filters.query}
        onChange={(val) => {
          const value = (val || "").toUpperCase();
          setFilters((prev) => ({ ...prev, query: value }));
          debouncedSearch(value);
        }}
        onSearch={() => setShowSearchKeypad(false)}
        title={t("reglement.search") || "Recherche"}
      />

      {/* ✅ CLIENT MODAL */}
      <Modal isOpen={clientModal} toggle={() => setClientModal(false)} centered size="lg">
        <ModalHeader toggle={() => setClientModal(false)}>Choisir Client</ModalHeader>
        <ModalBody className="p-0">
          <div className="pos-picker">
            <Button
              color={!filters.clientId ? "primary" : "light"}
              className="pos-picker-item"
              onClick={() => {
                setFilters((f) => ({ ...f, clientId: null }));
                setClientModal(false);
              }}
            >
              Tous
            </Button>

            {clients.map((c) => {
              const label =
                c.typeClient === "PARTICULIER"
                  ? `${c.nom} ${c.prenom || ""}`
                  : c.entreprise;

              return (
                <Button
                  key={c.id}
                  color={filters.clientId === c.id ? "primary" : "light"}
                  className="pos-picker-item"
                  onClick={() => {
                    setFilters((f) => ({ ...f, clientId: c.id }));
                    setClientModal(false);
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </ModalBody>
      </Modal>

    </Container>
  );
};

export default AvanceClientList;
